import React from 'react';
import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY } from '../data/taxonomies.js';
import { EuropeMap } from './europe_map.jsx';
import { OrgCard } from './org_views.jsx';
import { EuropeChoroplethMap } from './europe_choropleth_map.jsx';
import { TypologyWheel } from './wheel.jsx';
const { useState } = React;

// ============================================================
// Views: Map, Wheels, List
// ============================================================

export function MapView({ orgs, counts, maxCount, selectedCountries, onToggleCountry, onSelectOrg, mapStyle, accentColor, emptyState }) {
  const activeColor = accentColor || '#D97757'; // warm clay accent
  const hasSel = selectedCountries && selectedCountries.size > 0;
  return (
    <div className="map-view stacked">
      <div className="map-pane map-pane-fixed">
        <div className="map-header">
          <div>
            <h2>Europe</h2>
            <p className="muted">Click countries to filter (multi-select). Level of shading = density of matching organisations. Scroll below to browse through organisations that match your criteria.


            </p>
          </div>
          <div className="map-legend">
            <span className="muted">Orgs</span>
            <div className="legend-swatches">
              {[{ c: '#F3E2B3', l: '1–2' }, { c: '#EDC873', l: '3–5' }, { c: '#E0A53C', l: '6–10' },
              { c: '#CE7A2A', l: '11–20' },
              { c: '#B0531F', l: '21–40' },
              { c: '#7E2E12', l: '41+' }].
              map((b, i) =>
              <div key={i} className="legend-sw" style={{ background: b.c }} title={b.l} />
              )}
            </div>
            <span className="muted">41+</span>
          </div>
        </div>
        <div className="map-canvas-wrap">
          {mapStyle === 'geographic' ?
          <EuropeChoroplethMap counts={counts} maxCount={maxCount} selectedCountries={selectedCountries}
          onToggleCountry={onToggleCountry} activeColor={activeColor} /> :
          <EuropeMap counts={counts} maxCount={maxCount} selectedCountries={selectedCountries}
          onToggleCountry={onToggleCountry} activeColor={activeColor} />}
        </div>
      </div>
      <MapListBelow orgs={orgs} hasSel={hasSel} selectedCountries={selectedCountries} onSelectOrg={onSelectOrg} emptyState={emptyState} />
    </div>);

}

export function MapListBelow({ orgs, hasSel, selectedCountries, onSelectOrg, emptyState }) {
  const [shown, setShown] = useState(50);
  React.useEffect(() => {setShown(50);}, [orgs]);
  const visible = orgs.slice(0, shown);
  const remaining = orgs.length - shown;
  return (
    <div className="map-list map-list-below">
      <div className="map-list-head">
        {hasSel ? <>
          <span className="pin-icon">📍</span>
          <strong>{[...selectedCountries].join(', ')}</strong>
          <span className="muted">· {orgs.length} orgs</span>
        </> : <>
          <strong>All organisations</strong>
          <span className="muted">· {orgs.length} matching filters</span>
        </>}
      </div>
      <div className="org-grid-below">
        {visible.map((org) => <OrgCard key={org.id} org={org} onClick={() => onSelectOrg(org)} />)}
        {orgs.length === 0 && (emptyState || <div className="empty-state">No organisations match these filters.</div>)}
      </div>
      {remaining > 0 &&
      <div className="load-more-row">
          <button className="btn-secondary" onClick={() => setShown((s) => s + 50)}>
            Load {Math.min(50, remaining)} more <span className="muted">· {remaining} remaining</span>
          </button>
          {remaining > 50 &&
        <button className="btn-ghost" onClick={() => setShown(orgs.length)}>Show all</button>
        }
        </div>
      }
    </div>);

}

export function ListView({ orgs, onSelectOrg, emptyState, sortBy }) {
  const PAGE = 50;
  const [visible, setVisible] = React.useState(PAGE);
  React.useEffect(() => {setVisible(PAGE);}, [orgs]);

  const shown = orgs.slice(0, visible);
  const groupMode =
  sortBy === 'country-most' || sortBy === 'country-least' ? 'country' :
  sortBy === 'activity' ? 'activity' :
  sortBy === 'theme' ? 'theme' :
  'none';

  const keyFor = (o) => {
    if (groupMode === 'country') return o.country || 'Other';
    if (groupMode === 'activity') return o.activities && o.activities[0] || 'Other';
    if (groupMode === 'theme') return o.themes && o.themes[0] || 'Other';
    return '__all__';
  };

  // Total per-group counts across the entire filtered set (not just the paginated slice).
  const groupTotals = {};
  orgs.forEach((o) => {const k = keyFor(o);groupTotals[k] = (groupTotals[k] || 0) + 1;});

  const groups = {};
  const groupOrder = [];
  shown.forEach((o) => {
    const k = keyFor(o);
    if (!groups[k]) {groups[k] = [];groupOrder.push(k);}
    groups[k].push(o);
  });

  const remaining = Math.max(0, orgs.length - visible);
  const nextStep = Math.min(PAGE, remaining);

  return (
    <div className="list-view">
      {groupOrder.map((groupKey) => {
        const list = groups[groupKey];
        return (
          <div key={groupKey} className="list-country-group">
            {groupMode !== 'none' &&
            <div className="list-country-head">
                <h3>{groupKey}</h3>
                <span className="muted">{groupTotals[groupKey]}</span>
              </div>
            }
            <div className="list-grid">
              {list.map((org) => <OrgCard key={org.id} org={org} onClick={() => onSelectOrg(org)} />)}
            </div>
          </div>);

      })}
      {orgs.length === 0 && (emptyState || <div className="empty-state">No organisations match these filters.</div>)}
      {orgs.length > 0 &&
      <div className="list-pagination">
          <div className="list-pagination-info">
            Showing <strong>{shown.length}</strong> of <strong>{orgs.length.toLocaleString()}</strong> organisations
          </div>
          {remaining > 0 ?
        <div className="list-pagination-actions">
              <button className="btn-secondary" onClick={() => setVisible((v) => v + PAGE)}>
                Load {nextStep} more
              </button>
              {orgs.length > visible + PAGE &&
          <button className="btn-ghost" onClick={() => setVisible(orgs.length)}>
                  Show all
                </button>
          }
            </div> :

        <div className="muted list-pagination-end">End of list</div>
        }
        </div>
      }
    </div>);

}

export function WheelsView({ selectedActivities, selectedThemes, toggleActivity, toggleTheme, toggleMainActivity, toggleMainTheme, orgs, allOrgs, onSelectOrg }) {
  const [shown, setShown] = useState(40);
  React.useEffect(() => {setShown(40);}, [orgs]);
  const visible = orgs.slice(0, shown);
  const remaining = orgs.length - shown;
  return (
    <div className="wheels-view">
      <div className="wheels-head">
        <p>Click a <strong>main category</strong> to select it plus all its sub-categories. Click individual <strong>sub-categories</strong> to toggle them.</p>
        <p>Each additional selection within activities or themes adds to your results rather than narrowing them, but once you toggle both activity and thematic categories, the list will be narrowed to organisations that suit both sets of criteria<em></em>. Scroll below to browse through organisations that match your criteria. </p>
      </div>
      <div className="wheels-grid">
        <TypologyWheel
          title="Activity-based typology"
          subtitle="What organisations do"
          typology={ACTIVITY_TYPOLOGY}
          selected={selectedActivities}
          onToggle={toggleActivity}
          onToggleMain={toggleMainActivity}
          orgs={allOrgs}
          matchSub={(org, sub) => org.activities.some((a) => a.toLowerCase().includes(sub.toLowerCase()))} />
        
        <TypologyWheel
          title="Thematic typology"
          subtitle="What they work on"
          typology={THEME_TYPOLOGY}
          selected={selectedThemes}
          onToggle={toggleTheme}
          onToggleMain={toggleMainTheme}
          orgs={allOrgs}
          matchSub={(org, sub) => org.themes.some((a) => a.toLowerCase().includes(sub.toLowerCase()))} />
        
      </div>
      <div className="wheels-results">
        <div className="wheels-results-head">
          <strong>{orgs.length}</strong> organisations match current selection
        </div>
        <div className="wheels-results-grid">
          {visible.map((org) => <OrgCard key={org.id} org={org} onClick={() => onSelectOrg(org)} />)}
        </div>
        {remaining > 0 &&
        <div className="load-more-row">
            <button className="btn-secondary" onClick={() => setShown((s) => s + 50)}>
              Load {Math.min(50, remaining)} more <span className="muted">· {remaining} remaining</span>
            </button>
            {remaining > 50 &&
          <button className="btn-ghost" onClick={() => setShown(orgs.length)}>Show all</button>
          }
          </div>
        }
      </div>
    </div>);

}
